import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RefreshCw, Search, ChevronDown, Clock, Database, Zap, Calendar, PlayCircle, CheckCircle2, XCircle } from "lucide-react";
import { format, addDays, addMinutes } from "date-fns";
import { toast } from "sonner";

interface SchedulingDebugEvent {
  id: string;
  created_at: string;
  stage: string;
  user_id: string | null;
  thread_id: string | null;
  mentorship_request_id: string | null;
  meeting_id: string | null;
  proposal_id: string | null;
  slot_id: string | null;
  timezone: string | null;
  payload: Record<string, unknown> | null;
}

interface TestResult {
  success: boolean;
  proposalId?: string;
  meetingId?: string;
  error?: string;
  rpcPayload?: Record<string, unknown>;
}

const STAGE_COLORS: Record<string, string> = {
  client_add_slot: "bg-blue-500",
  rpc_create_proposal: "bg-purple-500",
  db_slot_inserted: "bg-green-500",
  accept_slot: "bg-orange-500",
  edge_slot_fetch: "bg-yellow-500",
  zoom_payload: "bg-red-500",
};

const STAGE_ICONS: Record<string, React.ReactNode> = {
  client_add_slot: <Clock className="h-3 w-3" />,
  rpc_create_proposal: <Database className="h-3 w-3" />,
  db_slot_inserted: <Database className="h-3 w-3" />,
  accept_slot: <Zap className="h-3 w-3" />,
  edge_slot_fetch: <Database className="h-3 w-3" />,
  zoom_payload: <Calendar className="h-3 w-3" />,
};

export function SchedulingDebugManager() {
  const [searchFilter, setSearchFilter] = useState("");
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [testMentorshipRequestId, setTestMentorshipRequestId] = useState("");
  const [testThreadId, setTestThreadId] = useState("");
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const { data: events, isLoading, refetch } = useQuery({
    queryKey: ["scheduling-debug-events", searchFilter],
    queryFn: async () => {
      let query = supabase
        .from("scheduling_debug_events")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(200);

      if (searchFilter) {
        // Search in thread_id, meeting_id, or slot_id
        query = query.or(
          `thread_id.eq.${searchFilter},meeting_id.eq.${searchFilter},slot_id.eq.${searchFilter},mentorship_request_id.eq.${searchFilter}`
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as SchedulingDebugEvent[];
    },
  });

  // Test proposal creation mutation
  const testProposalMutation = useMutation({
    mutationFn: async ({ mentorshipRequestId, threadId }: { mentorshipRequestId: string; threadId: string }) => {
      // Create a test slot 1 day from now
      const testSlotStart = addDays(new Date(), 1);
      testSlotStart.setHours(14, 0, 0, 0); // 2pm
      const testSlotEnd = addMinutes(testSlotStart, 30);

      const rpcPayload = {
        p_mentorship_request_id: mentorshipRequestId,
        p_thread_id: threadId,
        p_duration_minutes: 30,
        p_timezone: "America/Jamaica",
        p_note: "Admin test proposal",
        p_slots: [
          {
            start_at: testSlotStart.toISOString(),
            end_at: testSlotEnd.toISOString(),
          },
        ],
      };

      console.log("[SchedulingDebugManager] Running test proposal with payload:", rpcPayload);

      const { data, error } = await supabase.rpc("create_scheduled_meeting_proposal", rpcPayload);

      if (error) {
        console.error("[SchedulingDebugManager] Test proposal failed:", error);
        return {
          success: false,
          error: `${error.code}: ${error.message}`,
          rpcPayload,
        };
      }

      console.log("[SchedulingDebugManager] Test proposal succeeded:", data);
      
      // RPC now returns jsonb: { meeting_id, proposal_id, slots_inserted }
      const responseData = data as { meeting_id?: string; proposal_id?: string; slots_inserted?: number } | null;
      const meetingId = responseData?.meeting_id || "";
      const proposalId = responseData?.proposal_id || "";

      return {
        success: true,
        meetingId,
        proposalId,
        rpcPayload,
      };
    },
    onSuccess: (result) => {
      setTestResult(result);
      if (result.success) {
        toast.success(`Test proposal created! Meeting ID: ${result.meetingId}`);
        refetch();
      } else {
        toast.error(`Test failed: ${result.error}`);
      }
    },
    onError: (error) => {
      const errorMsg = error instanceof Error ? error.message : String(error);
      setTestResult({ success: false, error: errorMsg });
      toast.error(`Test failed: ${errorMsg}`);
    },
  });

  const handleRunTest = () => {
    if (!testMentorshipRequestId || !testThreadId) {
      toast.error("Please enter both mentorship request ID and thread ID");
      return;
    }
    setTestResult(null);
    testProposalMutation.mutate({ 
      mentorshipRequestId: testMentorshipRequestId, 
      threadId: testThreadId 
    });
  };

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedEvents(newExpanded);
  };

  const formatPayloadValue = (value: unknown): string => {
    if (value === null || value === undefined) return "null";
    if (typeof value === "object") return JSON.stringify(value, null, 2);
    return String(value);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Scheduling Debug Trail
            </CardTitle>
            <CardDescription>
              Pipeline tracing for scheduled meeting time resolution
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Test Proposal Creation */}
          <Card className="bg-muted/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <PlayCircle className="h-4 w-4" />
                Create Proposal Test
              </CardTitle>
              <CardDescription className="text-xs">
                Test the RPC function directly with a sample payload
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-muted-foreground">Mentorship Request ID</label>
                  <Input
                    placeholder="UUID..."
                    value={testMentorshipRequestId}
                    onChange={(e) => setTestMentorshipRequestId(e.target.value)}
                    className="text-xs"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Thread ID</label>
                  <Input
                    placeholder="UUID..."
                    value={testThreadId}
                    onChange={(e) => setTestThreadId(e.target.value)}
                    className="text-xs"
                  />
                </div>
              </div>
              <Button 
                size="sm" 
                onClick={handleRunTest}
                disabled={testProposalMutation.isPending || !testMentorshipRequestId || !testThreadId}
              >
                {testProposalMutation.isPending ? (
                  <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <PlayCircle className="h-3 w-3 mr-1" />
                )}
                Run Proposal Creation Test
              </Button>

              {testResult && (
                <Alert variant={testResult.success ? "default" : "destructive"} className="mt-2">
                  <div className="flex items-center gap-2">
                    {testResult.success ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    <AlertDescription className="text-xs">
                      {testResult.success ? (
                        <span>
                          <strong>Success!</strong> Meeting ID: <code>{testResult.meetingId}</code>
                          {testResult.proposalId && (
                            <>, Proposal ID: <code>{testResult.proposalId}</code></>
                          )}
                        </span>
                      ) : (
                        <span>
                          <strong>Failed:</strong> {testResult.error}
                        </span>
                      )}
                    </AlertDescription>
                  </div>
                  {testResult.rpcPayload && (
                    <pre className="mt-2 p-2 bg-background rounded text-xs overflow-x-auto max-h-32">
                      {JSON.stringify(testResult.rpcPayload, null, 2)}
                    </pre>
                  )}
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Search */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filter by thread_id, meeting_id, or slot_id..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="pl-10"
              />
            </div>
            {searchFilter && (
              <Button variant="ghost" size="sm" onClick={() => setSearchFilter("")}>
                Clear
              </Button>
            )}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-2 text-xs">
            {Object.entries(STAGE_COLORS).map(([stage, color]) => (
              <div key={stage} className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded-full ${color}`} />
                <span className="text-muted-foreground">{stage}</span>
              </div>
            ))}
          </div>

          {/* Events */}
          <ScrollArea className="h-[500px]">
            {isLoading ? (
              <div className="text-center text-muted-foreground py-8">Loading...</div>
            ) : !events || events.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                No debug events found. Schedule a meeting to generate trace data.
              </div>
            ) : (
              <div className="space-y-2">
                {events.map((event) => (
                  <Collapsible
                    key={event.id}
                    open={expandedEvents.has(event.id)}
                    onOpenChange={() => toggleExpand(event.id)}
                  >
                    <div className="border rounded-lg p-3 hover:bg-muted/50">
                      <CollapsibleTrigger className="w-full">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge
                              className={`${STAGE_COLORS[event.stage] || "bg-gray-500"} text-white`}
                            >
                              {STAGE_ICONS[event.stage]}
                              <span className="ml-1">{event.stage}</span>
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(event.created_at), "MMM d, HH:mm:ss.SSS")}
                            </span>
                          </div>
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${
                              expandedEvents.has(event.id) ? "rotate-180" : ""
                            }`}
                          />
                        </div>
                        
                        {/* Quick summary */}
                        <div className="flex flex-wrap gap-2 mt-2 text-xs">
                          {event.slot_id && (
                            <span className="text-muted-foreground">
                              slot: <code className="text-foreground">{event.slot_id.slice(0, 8)}...</code>
                            </span>
                          )}
                          {event.meeting_id && (
                            <span className="text-muted-foreground">
                              meeting: <code className="text-foreground">{event.meeting_id.slice(0, 8)}...</code>
                            </span>
                          )}
                          {event.timezone && (
                            <span className="text-muted-foreground">
                              tz: <code className="text-foreground">{event.timezone}</code>
                            </span>
                          )}
                        </div>
                      </CollapsibleTrigger>

                      <CollapsibleContent>
                        <div className="mt-3 pt-3 border-t space-y-2 text-xs">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-muted-foreground">Thread ID:</span>
                              <code className="ml-1 text-foreground">{event.thread_id || "—"}</code>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Meeting ID:</span>
                              <code className="ml-1 text-foreground">{event.meeting_id || "—"}</code>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Proposal ID:</span>
                              <code className="ml-1 text-foreground">{event.proposal_id || "—"}</code>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Slot ID:</span>
                              <code className="ml-1 text-foreground">{event.slot_id || "—"}</code>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Timezone:</span>
                              <code className="ml-1 text-foreground">{event.timezone || "—"}</code>
                            </div>
                            <div>
                              <span className="text-muted-foreground">User ID:</span>
                              <code className="ml-1 text-foreground">{event.user_id?.slice(0, 8) || "—"}...</code>
                            </div>
                          </div>

                          {event.payload && (
                            <div className="mt-2">
                              <span className="text-muted-foreground">Payload:</span>
                              <pre className="mt-1 p-2 bg-muted rounded text-xs overflow-x-auto">
                                {formatPayloadValue(event.payload)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </CardContent>
    </Card>
  );
}
